import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  CssBaseline,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import Topbar from "../../../components/topbar/TopBar";
import { useNavigate, useSearchParams } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save";
import { getPricingBaseListUseCase } from "../domain/usecases/getPricingBaseListUseCase";
import { createPricingBaseUseCase } from "../domain/usecases/createPricingBaseUseCase";
import { updatePricingBaseUseCase } from "../domain/usecases/updatePricingBaseUseCase";
import { ArrowBack } from "@mui/icons-material";

const NATIONALITIES = [
  { value: 1, label: "NACIONAL" },
  { value: 2, label: "EXTRANJERO" },
];

function createInitialForm(nationality = 1) {
  return {
    id: null,
    nationality,
    priceKm: "",
    priceTime: "",
    priceBase: "",
    active: true,
  };
}

function createFormFromRecord(record, nationality) {
  if (!record) {
    return createInitialForm(nationality);
  }

  return {
    id: record.id,
    nationality: Number(record.nationality ?? nationality),
    priceKm:
      record.priceKm === null || record.priceKm === undefined
        ? ""
        : String(record.priceKm),
    priceTime:
      record.priceTime === null || record.priceTime === undefined
        ? ""
        : String(record.priceTime),
    priceBase:
      record.priceBase === null || record.priceBase === undefined
        ? ""
        : String(record.priceBase),
    active: Boolean(record.active),
  };
}

function isSuccessResponse(response) {
  const message = response?.message?.toLowerCase() || "";
  return (
    response?.success === true ||
    message.includes("success") ||
    message.includes("created") ||
    message.includes("updated")
  );
}

const PricingBaseCreatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedNationality = Number(searchParams.get("nationality")) || 1;
  const [form, setForm] = useState(createInitialForm(requestedNationality));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pricingBaseList, setPricingBaseList] = useState([]);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  const loadPricingBaseList = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }

      const records = await getPricingBaseListUseCase();
      setPricingBaseList(records);

      const matchedRecord = records.find(
        (item) => Number(item.nationality) === requestedNationality,
      );

      setForm(createFormFromRecord(matchedRecord, requestedNationality));
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo cargar la configuracion base.",
      });
      setForm(createInitialForm(requestedNationality));
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadPricingBaseList();
  }, [requestedNationality]);

  const matchedRecord = useMemo(
    () =>
      pricingBaseList.find(
        (item) => Number(item.nationality) === requestedNationality,
      ) || null,
    [pricingBaseList, requestedNationality],
  );

  const isUpdateMode = Boolean(matchedRecord?.id);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckChange = (event) => {
    setForm((prev) => ({
      ...prev,
      active: event.target.checked,
    }));
  };

  const handleSave = async () => {
    const payload = {
      nationality: Number(form.nationality),
      priceKm: Number(form.priceKm),
      priceTime: Number(form.priceTime),
      priceBase: Number(form.priceBase),
      active: Boolean(form.active),
    };

    try {
      setSaving(true);

      if (isUpdateMode) {
        const response = await updatePricingBaseUseCase({
          id: matchedRecord.id,
          ...payload,
        });

        if (!isSuccessResponse(response)) {
          throw new Error("No se pudo actualizar el precio base.");
        }

        setMessage({
          open: true,
          type: "success",
          text: "Precio base actualizado correctamente.",
        });
        return;
      }

      const response = await createPricingBaseUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo crear el precio base.");
      }

      setMessage({
        open: true,
        type: "success",
        text: "Precio base creado correctamente.",
      });
      await loadPricingBaseList({ silent: true });
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo guardar el precio base.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="content">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": { color: "#000" },
            p: 1,
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 760,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1, md: 2 },
                  py: { xs: 2, md: 2 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ color: "#fff !important" }}
                    >
                      PRECIO BASE
                    </Typography>
                    <Typography
                      sx={{ mt: 0.75, color: "#fff !important", opacity: 0.88 }}
                    >
                      {isUpdateMode
                        ? "Actualiza la configuracion base para esta nacionalidad."
                        : "Configura los valores base por tipo de nacionalidad."}
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={<ArrowBack />}
                    onClick={() => navigate("/admin/tarifa-panel")}
                    sx={{
                      px: 2.25,
                      py: 1,
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.16)",
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.24)",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Volver
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 1, md: 3 } }}>
                {loading ? (
                  <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography color="text.secondary">
                      Cargando precio base...
                    </Typography>
                  </Stack>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      mx: "auto",
                      maxWidth: 480,
                      borderRadius: 3,
                      border: "1px solid rgba(20, 83, 45, 0.12)",
                      p: { xs: 1, md: 2 },
                    }}
                  >
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        select
                        label="Nacionalidad"
                        name="nationality"
                        value={form.nationality}
                        onChange={handleChange}
                        disabled
                      >
                        {NATIONALITIES.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.value} - {item.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        fullWidth
                        type="number"
                        label="Precio por kilometro"
                        name="priceKm"
                        value={form.priceKm}
                        onChange={handleChange}
                        inputProps={{ min: "0", step: "0.01" }}
                      />
                      <TextField
                        fullWidth
                        type="number"
                        label="Precio por tiempo"
                        name="priceTime"
                        value={form.priceTime}
                        onChange={handleChange}
                        inputProps={{ min: "0", step: "0.01" }}
                      />
                      <TextField
                        fullWidth
                        type="number"
                        label="Precio base"
                        name="priceBase"
                        value={form.priceBase}
                        onChange={handleChange}
                        inputProps={{ min: "0", step: "0.01" }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.active}
                            onChange={handleCheckChange}
                            sx={{
                              color: "#165c53",
                              "&.Mui-checked": {
                                color: "#165c53",
                              },
                            }}
                          />
                        }
                        label="Activo"
                        sx={{
                          mx: 0,
                          "& .MuiFormControlLabel-label": {
                            color: "#fff",
                            fontWeight: 600,
                          },
                        }}
                      />

                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                        sx={{
                          mt: 1,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 700,
                          bgcolor: "#165c53",
                          color: "#fff",
                          "&:hover": {
                            bgcolor: "#12483f",
                          },
                        }}
                      >
                        {saving
                          ? isUpdateMode
                            ? "Actualizando..."
                            : "Guardando..."
                          : isUpdateMode
                            ? "Actualizar"
                            : "Guardar"}
                      </Button>
                    </Stack>
                  </Paper>
                )}
              </Box>
            </Paper>
          </Stack>

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
};

export default PricingBaseCreatePage;
